import { gql, useMutation } from '@apollo/client';
import { omitBy, isNil } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { useBusinessInfoContext } from 'contexts/BusinessContextProvider';
import { useTrialsContext } from 'contexts/TrialsContextProvider';
import useQueryParams from 'hooks/useQueryParams';
import { subscriptionsPublicClient } from 'lib/apollo/clients';
import { getCookies } from 'lib/getCookies';
import { createCookie } from 'lib/getIdToken';
import isQABot from 'lib/isQABot';

export const TRACK_LEAD = gql`
  mutation trackLead($input: LeadInput!) {
    trackLead(input: $input)
  }
`;

const buildVariables = (variables) => {
  const nonNullValues = omitBy(variables, isNil);
  return { input: nonNullValues };
};

const getSessionId = () => {
  let sessionId = getCookies().podiumSessionId;

  if (!sessionId) {
    sessionId = uuidv4();
    const hostname = window.location.hostname;
    document.cookie = createCookie({
      cookieName: 'podiumSessionId',
      cookieValue: sessionId,
      hostname,
    });
  }
  return sessionId;
};

const getUrl = () => {
  const url = new URL(window.location.href);

  return `${url.origin}${url.pathname}`;
};

const useTrackLead = () => {
  const [mutate, { loading }] = useMutation(TRACK_LEAD, {
    client: subscriptionsPublicClient,
  });

  const {
    email,
    firstUtmMedium,
    firstName,
    googleClickId,
    lastName,
    partnerId,
    phone,
    salesforceCampaignId: campaignId,
    utmMedium,
    ssqt,
  } = useTrialsContext() || {};

  const {
    address = {},
    businessInfo = {},
    gmb = {},
  } = useBusinessInfoContext() || {};

  const { value: utm_source } = useQueryParams('utm_source');

  const podiumEmailRegex = /@podium\.com\s*$/;
  const isTest =
    utm_source === 'test' || isQABot() || podiumEmailRegex.test(email);

  const sessionId = getSessionId();
  const { name: businessName } = businessInfo;
  const { addressLines, administrativeArea, country, locality, postalCode } =
    address;
  const { placeId: googlePlaceId } = gmb;

  const variables = buildVariables({
    addressLines,
    administrativeArea,
    businessName,
    campaignId,
    country,
    email,
    firstName,
    firstUtmMedium,
    googleClickId,
    googlePlaceId,
    lastName,
    lastOffer: 'Free Trial: Abandoned Cart',
    lastOfferUrl: getUrl(),
    locality,
    partnerId,
    phone,
    postalCode,
    sessionId,
    test: isTest,
    utmMedium,
    ssqt,
  });

  const trackLead = async () => {
    if (!variables.input.sessionId) return;

    try {
      return await mutate({
        variables,
      });
    } catch (error) {
      window.newrelic?.noticeError?.(new Error(JSON.stringify(error)));
    }
  };

  return {
    trackLead,
    loading,
  };
};

export default useTrackLead;
